import React, { useId } from 'react';
import Clipped, { RectClipped } from './Clipped';
import { Side } from './model';
import { useBracketLayout } from './bracketLayout';

// Helper mejorado para obtener el escudo del equipo
function getShield(team) {
  // Verificar si el equipo existe y tiene la propiedad shield
  if (!team) return null;
  
  // Si tiene escudo directo, usarlo
  if (team.shield) {
    return team.shield;
  }
  
  return null;
}

export default function BracketGame(props) {
  const {
    game,
    hoveredTeamId,
    onHoveredTeamIdChange = () => {},
    styles = {},
    homeOnTop = true,
    topText = () => '',
    bottomText = () => '',
    // Filtrar props específicos de la librería para evitar warnings
    LineComponent,
    svgProps,
    ...rest
  } = props;

  const { isMobile, isSmallMobile, isTinyMobile, gameWidth, gameHeight } = useBracketLayout();
  const rawId = useId().replace(/:/g, '');

  /** Raíz del árbol = último partido del bracket (final de este cuadro). */
  const isBracketFinal = game?.isBracketRoot === true;

  const primary = '#0d9488';
  const primaryDark = '#0f766e';
  const gold = '#b45309';
  const goldLight = '#fcd34d';
  const ink = '#0f172a';
  const inkMuted = '#64748b';
  const loseColor = '#b45309';
  const surfaceStroke = 'rgba(15, 23, 42, 0.08)';
  const timePillBg = '#0f172a';
  const cardShadow = '0 1px 2px rgba(15, 23, 42, 0.05), 0 6px 20px rgba(15, 23, 42, 0.06)';
  const fontDisplay = "'DM Sans', 'Segoe UI', system-ui, sans-serif";
  const fontBody = "'DM Sans', 'Segoe UI', system-ui, sans-serif";

  const teamRowHeight = isTinyMobile ? 24 : isSmallMobile ? 27 : isMobile ? 30 : 34;
  const scoreBox = isTinyMobile ? 22 : isSmallMobile ? 24 : isMobile ? 26 : 30;
  const scoreWidth = scoreBox;
  const scoreHeight = scoreBox;
  const shieldSize = isTinyMobile ? 16 : isSmallMobile ? 18 : isMobile ? 20 : 24;
  const margin = 0;

  const teamNameFontSize = isTinyMobile ? 10 : isSmallMobile ? 11 : isMobile ? 12 : 18;
  const scoreFontSize = isTinyMobile ? 12 : isSmallMobile ? 14 : isMobile ? 16 : 18;
  const timeFontSize = isTinyMobile ? 10 : isSmallMobile ? 12 : isMobile ? 14 : 18;
  const fieldNameFontSize = isTinyMobile ? 9 : isSmallMobile ? 10 : isMobile ? 11 : 14;

  const {
    backgroundColor = 'rgba(255,255,255,0.72)',
    hoverBackgroundColor = 'rgba(13, 148, 136, 0.07)',
    scoreBackground = '#f1f5f9',
    teamNameStyle = {
      fill: ink,
      fontSize: teamNameFontSize,
      fontWeight: 600,
      fontFamily: fontBody,
    },
    teamScoreStyle = {
      fill: ink,
      fontSize: scoreFontSize,
      fontWeight: 600,
      fontFamily: fontBody,
      fontVariantNumeric: 'tabular-nums',
    },
    gameNameStyle = {
      fill: ink,
      fontSize: fieldNameFontSize,
      fontWeight: 600,
      fontFamily: fontBody,
    },
    gameTimeStyle = {
      fill: '#f8fafc',
      fontSize: timeFontSize,
      fontWeight: 600,
      fontFamily: fontBody,
      background: 'transparent',
      borderRadius: 6,
      padding: '2px 8px',
    },
    teamSeparatorStyle = {
      stroke: 'rgba(15, 23, 42, 0.08)',
      strokeWidth: 1,
    },
  } = styles;

  if (!game?.sides || typeof game.sides !== 'object') {
    return (
      <svg
        width={260}
        height={120}
        viewBox="0 0 260 120"
        style={{ margin, borderRadius: 18, boxShadow: cardShadow, background: 'transparent' }}
      >
        <text
          x="130"
          y="60"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: inkMuted, fontSize: 14, fontFamily: fontBody }}
        >
          Partido sin estructura (sides)
        </text>
      </svg>
    );
  }

  const sides = game.sides;
  const top = sides[homeOnTop ? Side.HOME : Side.VISITOR];
  const bottom = sides[homeOnTop ? Side.VISITOR : Side.HOME];

  // Verificar si el partido está en curso (activo)
  const isActive = game.isActive === true;
  
  // Determinar si hay un ganador
  const hasScores = top && bottom && top.score && bottom.score;
  const hasWinner = hasScores && top.score.score !== bottom.score.score;
  const isDrawResult = game.completed && hasScores && !hasWinner;
  const isTopWinner = hasWinner && top.score.score > bottom.score.score;
  const isBottomWinner = hasWinner && top.score.score < bottom.score.score;

  // Función segura para manejar hover
  const handleHover = (teamId) => {
    if (typeof onHoveredTeamIdChange === 'function') {
      onHoveredTeamIdChange(teamId);
    }
  };

  // Renderiza una fila de equipo con escudo, nombre y score
  const TeamRow = ({ x, y, side, onHover, isWinner, isLoser }) => {
    const tooltip = side.seed && side.team ? <title>{side.seed.displayName}</title> : null;
    const shield = side.team ? getShield(side.team) : null;
    
    // Función segura para los eventos de hover
    const handleTeamHover = (teamId) => {
      if (typeof onHover === 'function') {
        onHover(teamId);
      }
    };

    // Determinar estilo de texto basado en ganador/perdedor
    const isPlaceholder = !!(side.team && side.team.placeholder);
    const nameStyle = isWinner
      ? { ...teamNameStyle, fontWeight: 800, fill: primary }
      : isLoser
        ? { ...teamNameStyle, fill: inkMuted }
        : isPlaceholder
          ? { ...teamNameStyle, fontWeight: 600, fill: inkMuted, fontStyle: 'italic' }
          : { ...teamNameStyle, fontWeight: 600, fill: ink };
    
    // Determinar el valor del score
    const scoreValue = side.score ? side.score.score : '0';
    
    const scoreContainerX = gameWidth - scoreWidth - 8;
    const textMaxWidth = scoreContainerX - (shield ? shieldSize + 15 : 12) - 5;
    
    // Truncar nombre del equipo si es muy largo en móviles
    const teamName = side.team ? side.team.name : (side.seed ? side.seed.displayName : null);
    const maxNameLength = isTinyMobile ? 8 : isSmallMobile ? 10 : isMobile ? 12 : 26;
    const displayName = teamName && teamName.length > maxNameLength 
      ? teamName.substring(0, maxNameLength) + '...' 
      : teamName;
    
    return (
      <g
        onMouseEnter={() => handleTeamHover(side && side.team ? side.team.id : null)}
        onMouseLeave={() => handleTeamHover(null)}
        style={{ cursor: side.team ? 'pointer' : 'default' }}
      >
        {/* Área de toque más grande para móviles */}
        <rect 
          x={x} 
          y={y} 
          height={teamRowHeight} 
          width={gameWidth - 20} 
          fillOpacity={0}
          style={{ 
            minHeight: isMobile ? '32px' : 'auto',
            touchAction: 'manipulation'
          }}
        />
        <RectClipped x={x} y={y} height={teamRowHeight} width={textMaxWidth}>
          {/* Escudo - Usando SVG image para mejor compatibilidad */}
          {shield && (
            <image
              x={x + 5} 
              y={y + (teamRowHeight - shieldSize) / 2} 
              width={shieldSize} 
              height={shieldSize}
              href={shield}
              style={{
                visibility: 'visible',
                opacity: 1,
              }}
              preserveAspectRatio="xMidYMid meet"
              onError={() => {}}
              onLoad={() => {}}
            />
          )}
          {/* Nombre */}
          <text
            x={x + (shield ? shieldSize + 12 : 8)}
            y={y + teamRowHeight / 2 + teamNameFontSize / 3}
            style={nameStyle}
            textAnchor="start"
            dominantBaseline="middle"
          >
            {tooltip}
            {displayName}
          </text>
        </RectClipped>
        {/* Score Container */}
        <rect
          x={scoreContainerX}
          y={y + (teamRowHeight - scoreHeight) / 2}
          width={scoreWidth}
          height={scoreHeight}
          rx={Math.min(7, scoreHeight / 4)}
          ry={Math.min(7, scoreHeight / 4)}
          fill={isWinner ? 'rgba(15, 118, 110, 0.95)' : scoreBackground}
          stroke={isWinner ? gold : 'rgba(15,53,49,0.1)'}
          strokeWidth={isWinner ? 1.25 : 1}
        />
        {/* Score Text */}
        <text
          x={scoreContainerX + scoreWidth / 2}
          y={y + teamRowHeight / 2}
          style={{
            fontFamily: teamScoreStyle.fontFamily,
            fontWeight: teamScoreStyle.fontWeight,
            fontSize: scoreFontSize,
            fontVariantNumeric: teamScoreStyle.fontVariantNumeric,
            fill: isWinner
              ? goldLight
              : isLoser
                ? loseColor
                : isActive
                  ? primaryDark
                  : teamScoreStyle.fill,
          }}
          textAnchor="middle"
          dominantBaseline="middle"
          className={isActive ? 'bracket-score-live' : (isWinner ? 'bracket-winner-score' : isLoser ? 'bracket-loser-score' : '')}
        >
          {scoreValue}
        </text>
      </g>
    );
  };

  const topHovered = top && top.team && top.team.id === hoveredTeamId;
  const bottomHovered = bottom && bottom.team && bottom.team.id === hoveredTeamId;

  const svgStateClass = [
    isActive ? 'bracket-game-live' : '',
    game.completed && hasWinner ? 'bracket-game-finished' : '',
    isDrawResult ? 'bracket-game-draw' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const timeBoxWidth = isTinyMobile ? 58 : isSmallMobile ? 68 : isMobile ? 76 : 84;
  const timeBoxHeight = isTinyMobile ? 18 : isSmallMobile ? 20 : isMobile ? 22 : 24;
  const trophySlot = isBracketFinal ? 20 : 0;
  const headerClusterW = trophySlot + timeBoxWidth;
  const headerStartX = (gameWidth - headerClusterW) / 2;
  const timePillX = headerStartX + trophySlot;
  const hasField = !!(game.fieldName && String(game.fieldName).trim());
  const fieldStr = hasField ? String(game.fieldName).trim() : '';
  const footerReserve = fieldNameFontSize + 14;
  const contentY = timeBoxHeight + 14;
  const contentHeight = gameHeight - contentY - footerReserve;
  const separatorY = contentY + contentHeight / 2;
  const matchTitle = (game.name || bottomText(game) || 'Partido').trim();

  const gid = `b${rawId}`;

  return (
    <svg
      width={gameWidth}
      height={gameHeight}
      viewBox={`0 0 ${gameWidth} ${gameHeight}`}
      style={{
        borderRadius: 14,
        boxShadow: cardShadow,
        background: 'transparent',
        margin: margin,
        touchAction: 'manipulation',
        userSelect: 'none',
        transition: 'box-shadow 0.25s ease',
      }}
      {...rest}
      className={[
        'bracket-game-card',
        isActive ? 'bracket-game-active' : '',
        isBracketFinal ? 'final-match' : '',
        svgStateClass,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <defs>
        <linearGradient id={`${gid}-surface`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#f1f5f9" />
        </linearGradient>
        <linearGradient id={`${gid}-gold`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#92400e" />
          <stop offset="45%" stopColor="#eab308" />
          <stop offset="100%" stopColor="#ca8a04" />
        </linearGradient>
        <linearGradient id={`${gid}-topshine`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(13,148,136,0.12)" />
          <stop offset="100%" stopColor="rgba(13,148,136,0)" />
        </linearGradient>
        <filter id={`${gid}-dropshadow`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0f172a" floodOpacity="0.06" />
        </filter>
      </defs>

      <rect
        x="0"
        y="0"
        width={gameWidth}
        height={gameHeight}
        rx="14"
        ry="14"
        fill={`url(#${gid}-surface)`}
        filter={`url(#${gid}-dropshadow)`}
      />
      {isBracketFinal ? (
        <rect
          x="1.5"
          y="1.5"
          width={gameWidth - 3}
          height={gameHeight - 3}
          rx="14"
          ry="14"
          fill="none"
          stroke={`url(#${gid}-gold)`}
          strokeWidth="1.75"
          opacity="0.92"
        />
      ) : (
        <rect
          x="1"
          y="1"
          width={gameWidth - 2}
          height={gameHeight - 2}
          rx="14"
          ry="14"
          fill="none"
          stroke={surfaceStroke}
          strokeWidth="1"
        />
      )}

      {isBracketFinal ? (
        <svg
          x={headerStartX}
          y={6 + timeBoxHeight / 2 - 8}
          width={16}
          height={16}
          viewBox="0 0 24 24"
          className="bracket-final-trophy"
          aria-hidden
        >
          <title>Final del bracket</title>
          <path
            fill={gold}
            d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H8v2h8v-2h-3v-3.06c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z"
          />
        </svg>
      ) : null}

      {/* Hora */}
      <rect
        x={timePillX}
        y="6"
        width={timeBoxWidth}
        height={timeBoxHeight}
        rx="8"
        ry="8"
        fill={timePillBg}
        className={isActive ? 'bracket-time-background-live' : ''}
      />
      {isActive && (
        <circle cx={timePillX + 11} cy={6 + timeBoxHeight / 2} r="3" fill="#f87171" className="bracket-live-dot" />
      )}
      <text
        x={timePillX + timeBoxWidth / 2 + (isActive ? 6 : 0)}
        y={6 + timeBoxHeight / 2 + timeFontSize / 3}
        textAnchor="middle"
        dominantBaseline="middle"
        style={gameTimeStyle}
        className={isActive ? 'bracket-time-live' : ''}
      >
        {topText(game)}
      </text>

      {/* Área equipos */}
      <rect
        x="0"
        y={contentY}
        width={gameWidth}
        height={contentHeight}
        fill={backgroundColor}
        rx="12"
        ry="12"
        className={isActive ? 'bracket-background-active' : ''}
      />
      <rect
        x="12"
        y={contentY + 3}
        width={gameWidth - 24}
        height="2"
        rx="1"
        fill={`url(#${gid}-topshine)`}
        opacity="0.85"
      />

      {hasWinner &&
        (isTopWinner ? (
          <rect
            x="0"
            y={contentY}
            width={gameWidth}
            height={teamRowHeight}
            fill="rgba(13, 148, 136, 0.1)"
            rx="12"
            ry="12"
          />
        ) : (
          <rect
            x="0"
            y={separatorY}
            width={gameWidth}
            height={teamRowHeight}
            fill="rgba(13, 148, 136, 0.1)"
            rx="12"
            ry="12"
          />
        ))}

      {!isTopWinner && (
        <rect
          x="0"
          y={contentY}
          width={gameWidth}
          height={teamRowHeight}
          fill={topHovered ? hoverBackgroundColor : 'transparent'}
          rx="12"
          ry="12"
        />
      )}
      {!isBottomWinner && (
        <rect
          x="0"
          y={separatorY}
          width={gameWidth}
          height={teamRowHeight}
          fill={bottomHovered ? hoverBackgroundColor : 'transparent'}
          rx="12"
          ry="12"
        />
      )}
      
      {/* Equipos */}
      {top ? <TeamRow x={0} y={contentY} side={top} onHover={handleHover} isWinner={isTopWinner} isLoser={isBottomWinner} /> : null}
      {bottom ? <TeamRow x={0} y={separatorY} side={bottom} onHover={handleHover} isWinner={isBottomWinner} isLoser={isTopWinner} /> : null}
      
      {/* Separador */}
      <line x1="0" y1={separatorY} x2={gameWidth} y2={separatorY} style={teamSeparatorStyle} />
      
      {/* Pie: fase y campo en una línea (ej. "Cuartos H — CAMPO 1") */}
      <text
        x={gameWidth / 2}
        y={gameHeight - 10}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: fieldNameFontSize,
          fontWeight: 600,
          fontFamily: fontBody,
          letterSpacing: '0.02em',
        }}
        className="bracket-match-footer-line"
      >
        <title>{hasField ? `${matchTitle} — ${fieldStr}` : matchTitle}</title>
        <tspan fill={ink}>{matchTitle}</tspan>
        {hasField ? <tspan fill={inkMuted}>{` — ${fieldStr}`}</tspan> : null}
      </text>
    </svg>
  );
} 